import { cva, cx } from '@zenchef/styled-system/css'
import { styled } from '@zenchef/styled-system/jsx'
import { ComponentProps, RecipeVariantProps } from '@zenchef/styled-system/types'
import { ElementRef, forwardRef } from 'react'

import { useButtonGroupContext } from '@/components/redesign/common/ButtonGroup'
import getTestId from '@/utils/getTestId'

export const legacyButtonStyle = cva({
  base: {
    borderRadius: 'l',
    textStyle: 'title.s',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    verticalAlign: 'middle',
    gap: 'gap.2',
    cursor: {
      base: 'pointer',
      _disabled: 'not-allowed'
    },
    _focusVisible: {
      outline: 'none'
    }
  },
  variants: {
    shape: {
      rounded: {
        borderRadius: 'pill',
        width: '32px',
        height: '32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }
    },
    // visual are styles (deprecated) for buttons from Milan Designs (january24 redesign)
    visual: {
      transparent: {
        backgroundColor: {
          base: 'background.neutral-on-brand.base.subtler',
          _hover: 'background.neutral-on-brand.base.subtler-hover',
          _active: 'background.neutral-on-brand.base.bold',
          _disabled: 'background.neutral-on-brand.base.subtler'
        },
        color: {
          base: 'content.neutral-on-brand.bold',
          _active: {
            _lowContrast: 'content.neutral.reverse.bold',
            _highContrast: 'content.neutral.base.bold'
          }
        }
      },
      solid: {
        backgroundColor: 'background.neutral-on-brand.base.bold',
        color: {
          _lowContrast: 'content.neutral.reverse.bold',
          _highContrast: 'content.neutral.base.bold'
        },
        opacity: {
          _disabled: {
            _lowContrast: '40%',
            _highContrast: '50%'
          }
        }
      },
      restaurant: {
        backgroundColor: {
          base: 'background.brand.bold',
          _hover: 'background.brand.bold-hover',
          _active: 'background.brand.bold-active',
          _disabled: 'background.brand.disabled'
        },
        color: {
          base: 'content.neutral-on-brand.bold'
        }
      }
    },
    size: {
      small: {
        px: 'padding.3',
        py: 'padding.2'
      }
    },
    iconOnly: {
      true: {
        height: 'xl',
        width: 'xl',
        _aromaticonChildren: {
          fontSize: 'icon.base'
        }
      }
    }
  }
})

const StyledButton = styled('button', legacyButtonStyle)

export type ButtonVariantProps = RecipeVariantProps<typeof legacyButtonStyle>

export type ButtonProps = TestingAttributes & ComponentProps<typeof StyledButton>

/** @deprecated - for new interfaces, use <Button> from @zenchef/ds-react instead */
export const LegacyButton = forwardRef<ElementRef<'button'>, ButtonProps>(
  ({ testId, className: classNameFromProps, ...props }, ref) => {
    const { className: classNameFromContext, ...restPropsFromContext } = useButtonGroupContext()
    return (
      <StyledButton
        {...restPropsFromContext}
        {...props}
        className={cx(classNameFromContext, classNameFromProps)}
        data-testid={getTestId(testId, 'btn')}
        ref={ref}
      />
    )
  }
)
